import { Check, Clock3, X } from "lucide-react";

import { useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import { Button } from "../primitives";
import type { AgentWorkbenchEventOf } from "./activity-model";

export type AgentWorkbenchApprovalData =
  AgentWorkbenchEventOf<"approval">["payload"];

export interface AgentWorkbenchApprovalIntent {
  approvalId: string;
  approved: boolean;
}

export interface AgentWorkbenchApprovalProps {
  approval: AgentWorkbenchApprovalData;
  className?: string;
  onDecision?: (intent: AgentWorkbenchApprovalIntent) => void;
}

function JsonValue({ value }: { value: unknown }) {
  if (value === undefined) return null;
  const content =
    typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <pre
      className="max-h-48 max-w-full overflow-auto overscroll-contain whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs text-foreground [overflow-wrap:anywhere]"
      tabIndex={0}
    >
      {content}
    </pre>
  );
}

export function AgentWorkbenchApproval({
  approval,
  className,
  onDecision,
}: AgentWorkbenchApprovalProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.approval;
  const requested = approval.status === "requested";
  const approved = approval.status === "approved";

  return (
    <article
      className={cn(
        "min-w-0 overflow-hidden rounded-lg border border-border bg-card p-3 text-card-foreground",
        className,
      )}
      data-agent-approval-status={approval.status}
    >
      <div className="flex items-start gap-3">
        {requested ? (
          <Clock3
            className="mt-0.5 size-4 shrink-0 text-warning-foreground"
            aria-hidden="true"
          />
        ) : approved ? (
          <Check
            className="mt-0.5 size-4 shrink-0 text-success"
            aria-hidden="true"
          />
        ) : (
          <X
            className="mt-0.5 size-4 shrink-0 text-destructive-text"
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-sm font-medium">{approval.title}</h3>
          {approval.description ? (
            <p
              tabIndex={0}
              className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words text-sm text-muted-foreground [overflow-wrap:anywhere]"
            >
              {approval.description}
            </p>
          ) : null}
          {approval.requestedAction !== undefined ? (
            <div className="mt-3 min-w-0">
              <JsonValue value={approval.requestedAction} />
            </div>
          ) : null}
          {requested ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                className="h-auto px-3 py-1.5 text-sm"
                disabled={!onDecision}
                onClick={() =>
                  onDecision?.({
                    approvalId: approval.approvalId,
                    approved: true,
                  })
                }
              >
                {messages.approve}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto px-3 py-1.5 text-sm"
                disabled={!onDecision}
                onClick={() =>
                  onDecision?.({
                    approvalId: approval.approvalId,
                    approved: false,
                  })
                }
              >
                {messages.reject}
              </Button>
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              {approved ? messages.approved : messages.rejected}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
